import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { refreshUserFromDB } from '@/lib/customAuth';

type TabType = 'all' | 'income' | 'expense';

interface PointsLog {
  id: string;
  delta: number;
  type: 'income' | 'expense';
  source: string;
  remark: string | null;
  balance_after: number;
  created_at: string;
}

const sourceLabel: Record<string, string> = {
  manual: '后台调整',
  order: '订单消费',
  recharge: '充值获得',
  transfer_in: '积分转入',
  transfer_out: '积分转出',
  transfer: '积分转账',
  reward: '活动奖励',
  refund: '退款返还',
};

const sourceIcon: Record<string, string> = {
  manual: 'ri-settings-3-line',
  order: 'ri-shopping-bag-line',
  recharge: 'ri-add-circle-line',
  transfer_in: 'ri-arrow-down-circle-line',
  transfer_out: 'ri-arrow-up-circle-line',
  transfer: 'ri-exchange-line',
  reward: 'ri-trophy-line',
  refund: 'ri-refund-2-line',
};

const WalletPage = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { profile, loading } = useProfile();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [logs, setLogs] = useState<PointsLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!user) return;
    setLogsLoading(true);
    const { data } = await supabase
      .from('points_log')
      .select('id, delta, type, source, remark, balance_after, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100);
    if (data) setLogs(data as PointsLog[]);
    setLogsLoading(false);
  }, [user]);

  // 进入页面时强制从数据库刷新积分，确保转账后余额同步
  useEffect(() => {
    refreshUserFromDB().then(() => refresh());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // 优先用 user（实时刷新），profile 作为兜底
  const points = user?.points ?? profile?.points ?? 0;
  const balance = user?.balance ?? profile?.balance ?? 0;

  const totalIncome = logs.filter((l) => l.type === 'income' && l.source !== 'refund').reduce((s, l) => s + l.delta, 0);
  const totalExpense = logs.filter((l) => l.type === 'expense').reduce((s, l) => s + Math.abs(l.delta), 0);

  const fmt = (n: number) => n.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  const filteredLogs = logs.filter((l) => {
    if (activeTab === 'all') return true;
    return l.type === activeTab;
  });

  const formatDate = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return `今天 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    if (diffDays === 1) return `昨天 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    return `${d.getMonth() + 1}月${d.getDate()}日 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center max-w-[480px] mx-auto">
        <i className="ri-wallet-3-line text-5xl text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">请先登录</p>
        <button onClick={() => navigate('/login')} className="bg-rose-500 text-white px-6 py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap">
          去登录
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center px-4 h-14 gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-gray-700 text-xl" />
          </button>
          <h1 className="font-bold text-gray-900 text-base">我的积分</h1>
        </div>
      </header>

      {/* Points Card */}
      <div className="bg-gradient-to-r from-rose-500 to-rose-400 px-4 pt-6 pb-10">
        <p className="text-white/70 text-sm mb-1">积分余额</p>
        {loading ? (
          <div className="h-12 flex items-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-white font-bold text-4xl">{fmt(points)}</span>
            <span className="text-white/70 text-base">积分</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/shop')}
            className="flex-1 bg-white/20 text-white text-sm py-2.5 rounded-xl cursor-pointer whitespace-nowrap font-medium"
          >
            <i className="ri-gift-line mr-1" />去兑换
          </button>
          <button
            onClick={() => navigate('/transfer')}
            className="flex-1 bg-white/20 text-white text-sm py-2.5 rounded-xl cursor-pointer whitespace-nowrap font-medium"
          >
            <i className="ri-exchange-line mr-1" />积分转账
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mx-4 -mt-5 relative z-10 bg-white rounded-2xl overflow-hidden">
        <div className="grid grid-cols-3 divide-x divide-gray-100">
          <div className="p-4 text-center">
            <p className="text-base font-bold text-emerald-500">{fmt(points)}</p>
            <p className="text-xs text-gray-400 mt-0.5">当前积分</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-base font-bold text-rose-400">+{fmt(totalIncome)}</p>
            <p className="text-xs text-gray-400 mt-0.5">累计获得</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-base font-bold text-gray-500">-{fmt(totalExpense)}</p>
            <p className="text-xs text-gray-400 mt-0.5">累计消耗</p>
          </div>
        </div>
      </div>

      {/* Records */}
      <div className="bg-white mt-3 mx-4 rounded-2xl overflow-hidden mb-4">
        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          {([['all', '全部'], ['income', '收入'], ['expense', '支出']] as [TabType, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex-1 py-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === key ? 'text-rose-500 border-b-2 border-rose-500' : 'text-gray-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* List */}
        {logsLoading ? (
          <div className="flex items-center justify-center py-14">
            <div className="w-6 h-6 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <i className="ri-file-list-3-line text-4xl mb-3" />
            <p className="text-sm text-gray-500 font-medium mb-1">暂无积分明细</p>
            <p className="text-xs text-gray-400">积分变动记录将在此显示</p>
          </div>
        ) : (
          <ul>
            {filteredLogs.map((log, i) => {
              const isIncome = log.type === 'income';
              const icon = sourceIcon[log.source] ?? 'ri-copper-coin-line';
              const label = sourceLabel[log.source] ?? log.source;
              return (
                <li
                  key={log.id}
                  className={`flex items-center gap-3 px-4 py-3.5 ${i < filteredLogs.length - 1 ? 'border-b border-gray-50' : ''}`}
                >
                  {/* Icon */}
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${isIncome ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                    <i className={`${icon} text-base ${isIncome ? 'text-emerald-500' : 'text-rose-400'}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 font-medium truncate">{label}</p>
                    {log.remark && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{log.remark}</p>
                    )}
                    <p className="text-xs text-gray-300 mt-0.5">{formatDate(log.created_at)}</p>
                  </div>

                  {/* Delta + balance */}
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${isIncome ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {isIncome ? '+' : ''}{fmt(log.delta)}
                    </p>
                    <p className="text-xs text-gray-300 mt-0.5">余 {fmt(log.balance_after)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WalletPage;
